import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getDetailsFromFormFields, fetchDropdowns } from "../../../../utils/fn";
import { getToken } from "../../../../utils/auth";
import "./form.css";

// --- Updated MessageBox Component using a React Portal ---
const MessageBox = ({ title, message, onClose }) => {
  if (!message) {
    return null;
  }

  // createPortal takes two arguments: 1. The JSX to render, 2. The DOM element to render it into.
  return createPortal(
    <div className="message-box-overlay">
      <div className="message-box-content">
        <h3>{title}</h3>
        <p>{message}</p>
        <button onClick={onClose} className="message-box-button">
          OK
        </button>
      </div>
    </div>,
    document.body // This renders the modal directly on the body, escaping parent CSS constraints.
  );
};


function Form({
  formFields,
  submitApi,
  formHeading,
  validationRules = [],
  calculationRules = [],
  data = null,
}) {
  const [formDetails, setFormDetails] = useState({ fields: [], defaultValues: {} });
  const [dropdowns, setDropdowns] = useState({});
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({ fields: [], error: "" });
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const [apiGeneralError, setApiGeneralError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [messageBox, setMessageBox] = useState({ title: '', message: '' });
  
  // --- Initialize the navigate hook for redirection ---
  const navigate = useNavigate();

  useEffect(() => {
    const details = getDetailsFromFormFields(formFields);
    setFormDetails(details);
    setFormData(data || details.defaultValues);
  }, [formFields, data]);

  useEffect(() => {
    const loadDropdowns = async () => {
      if (formFields.length > 0 && Object.keys(dropdowns).length === 0) {
        setIsDropdownLoading(true);
        try {
          await fetchDropdowns(formFields, formData, setDropdowns);
        } catch (error) {
          console.error("Error fetching dropdown data:", error);
          setApiGeneralError("Failed to load form options. Please try again.");
        } finally {
            setIsDropdownLoading(false);
        }
      }
    };
    loadDropdowns();
  }, [formFields, formData, dropdowns]);

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : (value === "" ? null : value);
    setFormData(prevData => ({ ...prevData, [name]: finalValue }));

    if (formErrors.fields.includes(name)) {
      setFormErrors(prevErrors => ({
        ...prevErrors,
        fields: prevErrors.fields.filter((field) => field !== name),
      }));
    }
    setApiGeneralError("");
  };
  
  const validateForm = (dataToValidate) => {
    for (const rule of validationRules) {
      const result = rule.validator(dataToValidate);
      if (result) {
        return { isValid: false, errors: result };
      }
    }
    return { isValid: true, errors: { fields: [], error: "" } };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({ fields: [], error: "" });
    setApiGeneralError("");
    setIsLoading(true);

    const { isValid, errors } = validateForm(formData);
    if (!isValid) {
      setFormErrors(errors);
      setIsLoading(false);
      return;
    }

    const token = getToken();
    const method = data ? "PUT" : "POST";
    const apiUrl = data ? `${submitApi}/${data.id}` : submitApi;

    try {
      const response = await fetch(apiUrl, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });

      const responseData = await response.json();
      if (response.ok) {
        setMessageBox({
          title: "Success",
          message: `Customer details have been ${data ? "updated" : "saved"} successfully.`,
        });
      } else {
        console.error("API Error:", responseData);
        if (responseData.fields) {
          setFormErrors({
            fields: responseData.fields,
            error: responseData.error || "Please correct the highlighted fields.",
          });
        } else {
          setApiGeneralError(responseData.error || "An unexpected server error occurred.");
        }
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
      setApiGeneralError("Failed to connect to the server. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // --- Function to handle closing the dialog and redirecting ---
  const handleCloseMessageBox = () => {
    setMessageBox({ title: '', message: '' });
    navigate('/customers'); // Redirect to the customer list page
  };

  return (
    <div className="form-container">
      <MessageBox
        title={messageBox.title}
        message={messageBox.message}
        onClose={handleCloseMessageBox}
      />

      <h2>{data ? "Update" : "Add New"} {formHeading}</h2>
      <form onSubmit={handleSubmit} className="form">
        {apiGeneralError && <p className="form-general-error">{apiGeneralError}</p>}
        {formErrors.error && !apiGeneralError && <p className="form-general-error">{formErrors.error}</p>}

        {formDetails.fields.map((row, rowIndex) => (
          <div className="form-row" key={rowIndex}>
            {row.map((item) => (
              <FormField
                key={item.fieldName}
                item={item}
                dropdowns={dropdowns}
                formErrors={formErrors}
                formData={formData}
                handleChange={handleChange}
                isDropdownLoading={isDropdownLoading}
              />
            ))}
          </div>
        ))}
        <button type="submit" className="submit-button" disabled={isLoading}>
          {isLoading ? (data ? "Updating..." : "Submitting...") : (data ? "Update" : "Submit")}
        </button>
      </form>
    </div>
  );
}


function FormField({ item, dropdowns, formErrors, formData, handleChange, isDropdownLoading }) {
    const isCheckbox = item.tag === "input" && item.inputType === "checkbox";
    const hasError = formErrors.fields.includes(item.fieldName);
    const value = formData[item.fieldName] ?? '';

    return (
        <div className={`form-group ${isCheckbox ? "checkbox-group" : ""} ${hasError ? "has-error" : ""}`}>
            {!isCheckbox && (
                <label htmlFor={item.fieldName}>
                    {item.displayName}
                    {item.required && <span className="required">*</span>}
                </label>
            )}

            {item.tag === 'input' && !isCheckbox && (
                <input
                    type={item.inputType}
                    id={item.fieldName}
                    name={item.fieldName}
                    value={value}
                    onChange={handleChange}
                    required={item.required}
                    maxLength={item.maxLength}
                />
            )}

            {item.tag === 'select' && (
                <select
                    name={item.fieldName}
                    id={item.fieldName}
                    value={value}
                    onChange={handleChange}
                    required={item.required}
                >
                    <option value="">
                        {isDropdownLoading ? 'Loading...' : `Select ${item.displayName}`}
                    </option>
                    {dropdowns[item.fieldName]?.map((option) => (
                        <option key={option.id} value={option.id}>
                            {option.value}
                        </option>
                    ))}
                </select>
            )}

            {item.tag === 'textarea' && (
                <textarea
                    id={item.fieldName}
                    name={item.fieldName}
                    value={value}
                    onChange={handleChange}
                    required={item.required}
                    maxLength={item.maxLength}
                />
            )}
            
            {isCheckbox && (
                <>
                    <input
                        type="checkbox"
                        id={item.fieldName}
                        name={item.fieldName}
                        checked={!!value}
                        onChange={handleChange}
                    />
                    <label htmlFor={item.fieldName}>
                        {item.displayName}
                    </label>
                </>
            )}
        </div>
    );
}

export default Form;